import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getRSVPResponsesSync, clearCurrentRSVP } from '../utils/storage';

type Language = 'english' | 'iban';

interface WeddingInvitationProps {
  children: (selectedLanguage: Language) => React.ReactNode;
  onAnimationComplete?: () => void;
}

const WeddingInvitation: React.FC<WeddingInvitationProps> = ({ children, onAnimationComplete }) => {
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [isEnvelopeOpen, setIsEnvelopeOpen] = useState(false);
  const [showInvitation, setShowInvitation] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [hasExistingRSVP, setHasExistingRSVP] = useState(false);

  // Check for existing RSVP on component mount
  useEffect(() => {
    // Check if user wants to restart (via URL parameter)
    const urlParams = new URLSearchParams(window.location.search);
    const restart = urlParams.get('restart');
    
    if (restart === 'true') {
      // Clear existing RSVP and restart animation
      clearCurrentRSVP();
      setHasExistingRSVP(false);
      // Remove restart parameter from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    const existingResponses = getRSVPResponsesSync();
    if (existingResponses.length > 0) {
      setHasExistingRSVP(true);
      setSelectedLanguage('english');
      setShowContent(true);
    }
  }, []);

  // Function to restart the animation
  const restartAnimation = () => {
    setHasExistingRSVP(false);
    setShowContent(false);
    setShowInvitation(false);
    setIsEnvelopeOpen(false);
    setSelectedLanguage(null);
    clearCurrentRSVP();
  };

  // Add keyboard shortcut to restart (Ctrl+R or Cmd+R)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'r' && event.shiftKey) {
        event.preventDefault();
        restartAnimation();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const handleLanguageSelect = (language: Language) => {
    setSelectedLanguage(language);
  };

  const handleEnvelopeClick = () => {
    setIsEnvelopeOpen(true);
    setTimeout(() => {
      setShowInvitation(true);
    }, 800);
  };

  const handleInvitationClick = () => {
    setShowContent(true);
    if (onAnimationComplete) {
      onAnimationComplete();
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-sage-50 to-sage-100">
      {/* Language Selection Screen */}
      <AnimatePresence>
        {!selectedLanguage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gradient-to-br from-sage-100 via-sage-50 to-cream-50"
          >
            <div className="text-center space-y-8">
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-sage-800 mb-4">D&K</h1>
                <p className="text-sage-600 text-lg md:text-xl font-serif mb-8">Choose Your Language / Pilih Jaku Nuan</p>
              </motion.div>
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="space-y-4"
              >
                <button
                  onClick={() => handleLanguageSelect('english')}
                  className="block w-full max-w-xs mx-auto px-8 py-4 bg-gradient-to-r from-sage-600 to-sage-700 text-cream-100 rounded-lg shadow-lg hover:from-sage-700 hover:to-sage-800 transition-all duration-300 transform hover:scale-105 font-serif text-lg"
                >
                  English
                </button>
                <button
                  onClick={() => handleLanguageSelect('iban')}
                  className="block w-full max-w-xs mx-auto px-8 py-4 bg-gradient-to-r from-sage-600 to-sage-700 text-cream-100 rounded-lg shadow-lg hover:from-sage-700 hover:to-sage-800 transition-all duration-300 transform hover:scale-105 font-serif text-lg"
                >
                  Iban
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <AnimatePresence>
        {(showContent || hasExistingRSVP) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative z-10"
          >
            {children(selectedLanguage!)}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Wedding Invitation */}
      <AnimatePresence>
        {selectedLanguage && !showContent && !hasExistingRSVP && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Background Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-sage-100 via-sage-50 to-cream-50"></div>
            
            {/* Floating Decorative Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-4 h-4 bg-sage-200 rounded-full opacity-20"
                  style={{
                    left: `${20 + i * 15}%`,
                    top: `${10 + i * 12}%`,
                  }}
                  animate={{
                    y: [0, -20, 0],
                    opacity: [0.2, 0.4, 0.2],
                  }}
                  transition={{
                    duration: 3 + i * 0.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </div>

            {/* Envelope */}
            {!showInvitation && (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative cursor-pointer group"
                onClick={handleEnvelopeClick}
              >
                {/* Envelope Body */}
                <div className="relative w-80 h-56 md:w-96 md:h-64 lg:w-[28rem] lg:h-80 bg-gradient-to-br from-cream-100 to-cream-200 rounded-lg shadow-2xl border border-sage-200" style={{ perspective: '1000px' }}>
                  {/* Envelope Flap */}
                  <motion.div
                    className="absolute -top-1 left-0 right-0 h-32 md:h-36 lg:h-40 bg-gradient-to-br from-sage-200 to-sage-300 rounded-t-lg border border-sage-300"
                    style={{
                      clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
                      transformOrigin: '50% 0%'
                    }}
                    animate={isEnvelopeOpen ? {
                      rotateX: 180,
                      z: 20
                    } : {
                      rotateX: 0,
                      z: 0
                    }}
                    transition={{ duration: 0.8, ease: "easeInOut" }}
                  >
                    {/* Wax Seal */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-sage-600 to-sage-800 rounded-full shadow-lg flex items-center justify-center">
                      <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-sage-700 rounded-full flex items-center justify-center">
                        <span className="text-cream-100 text-xs md:text-sm lg:text-base font-serif">D&K</span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Envelope Content Preview */}
                  <div className="absolute inset-4 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 mx-auto mb-4 bg-sage-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 text-sage-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="text-sage-700 text-sm md:text-base lg:text-lg font-serif mb-2">
                        {selectedLanguage === 'iban' ? (
                          <p>Nuan Dikelaluka Datai</p>
                        ) : (
                          <p>You're Invited</p>
                        )}
                      </div>
                      <div className="text-sage-500 text-xs md:text-sm lg:text-base">
                        {selectedLanguage === 'iban' ? (
                          <p>Tekan ditu</p>
                        ) : (
                          <p>Click to open</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-t from-sage-100/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
                </div>

                {/* Click Instruction */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1, duration: 0.6 }}
                  className="absolute -bottom-16 left-0 right-0 flex flex-col items-center"
                >
                  <div className="text-sage-600 text-sm md:text-base lg:text-lg font-serif mb-2 text-center">
                    {selectedLanguage === 'iban' ? (
                      <p>Tekan ngambika nuan ulih meda surat mai datai</p>
                    ) : (
                      <p>Click to open your invitation</p>
                    )}
                  </div>
                  <motion.div
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="w-6 h-6 text-sage-400"
                  >
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </motion.div>
                </motion.div>
              </motion.div>
            )}

            {/* Invitation Letter */}
            <AnimatePresence>
              {showInvitation && (
                <motion.div
                  className="relative cursor-pointer group max-w-md md:max-w-2xl lg:max-w-3xl"
                  onClick={handleInvitationClick}
                  initial={{ 
                    rotateX: -90,
                    transformOrigin: "center top",
                    opacity: 0
                  }}
                  animate={{ 
                    rotateX: 0,
                    transformOrigin: "center top",
                    opacity: 1
                  }}
                  exit={{ 
                    rotateX: -90,
                    transformOrigin: "center top",
                    opacity: 0
                  }}
                  transition={{ 
                    type: "spring",
                    stiffness: 100,
                    damping: 20,
                    duration: 1.5
                  }}
                  style={{
                    perspective: "1000px",
                    transformStyle: "preserve-3d"
                  }}
                >
                  <div className="bg-gradient-to-br from-cream-50 to-cream-100 p-8 md:p-12 lg:p-16 rounded-lg shadow-2xl border border-sage-200 relative overflow-hidden">
                    {/* Decorative Border */}
                    <div className="absolute inset-2 border-2 border-sage-300 rounded-lg opacity-30"></div>
                    
                    {/* Content */}
                    <div className="relative z-10 text-center space-y-6 md:space-y-8 lg:space-y-10">
                      {/* Header */}
                      <div className="space-y-2">
                        {selectedLanguage === 'iban' ? (
                          <p className="text-sage-600 text-sm md:text-base lg:text-lg font-serif tracking-wider uppercase">Nuan dikelaluka datai ngagai tikah</p>
                        ) : (
                          <p className="text-sage-600 text-sm md:text-base lg:text-lg font-serif tracking-wider uppercase">You are cordially invited to the wedding of</p>
                        )}
                      </div>

                      {/* Names */}
                      <div className="space-y-4">
                        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-sage-800 tracking-wide">
                          Dominic
                        </h1>
                        <div className="flex items-center justify-center space-x-4">
                          <div className="w-12 md:w-16 lg:w-20 h-px bg-sage-400"></div>
                          <span className="text-sage-600 text-lg md:text-xl lg:text-2xl font-serif">&</span>
                          <div className="w-12 md:w-16 lg:w-20 h-px bg-sage-400"></div>
                        </div>
                        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-sage-800 tracking-wide">
                          Kemberly
                        </h1>
                      </div>

                      {/* Decorative Element */}
                      <div className="flex justify-center">
                        <svg className="w-16 h-8 md:w-20 md:h-10 lg:w-24 lg:h-12 text-sage-400" viewBox="0 0 100 40" fill="currentColor">
                          <path d="M20 20 Q30 10 40 20 Q50 30 60 20 Q70 10 80 20" stroke="currentColor" strokeWidth="1" fill="none" />
                          <circle cx="30" cy="18" r="2" />
                          <circle cx="50" cy="22" r="2" />
                          <circle cx="70" cy="18" r="2" />
                        </svg>
                      </div>

                      {/* Call to Action */}
                      <div className="space-y-2">
                        {selectedLanguage === 'iban' ? (
                          <>
                            <p className="text-sage-700 text-sm md:text-base lg:text-lg font-serif">Aram sama gaga ati enggau kami</p>
                            <p className="text-sage-500 text-xs md:text-sm lg:text-base font-serif italic">Tekan kena Ngesahka Penatai nuan</p>
                          </>
                        ) : (
                          <>
                            <p className="text-sage-700 text-sm md:text-base lg:text-lg font-serif">Please join us in celebrating our special day</p>
                            <p className="text-sage-500 text-xs md:text-sm lg:text-base font-serif italic">Click to RSVP</p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Hover Effect */}
                    <div className="absolute inset-0 bg-gradient-to-t from-sage-50/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WeddingInvitation;